import moment from 'moment';
import { getTemplateId } from 'helper/utils';
import {
	buildShrinkTimeWindow,
	buildForecastWk,
	buildCustomAttrList,
	buildPreviewSelectedList,
	getParentAndDftFilter
} from 'components/Preview/Preview.utils';

import {
	PARENT_CUST_ATTR,
	CHARTER,
	PROJECTS,
	SHARED,
	NOW,
	SCHEDULED,
	RUNNING,
	COMPLETED,
	FAILED,
	LATER
} from 'constants/constant';
import { FEATURES } from 'helper/basicFeatureFlag';

/**
 * This decides if the Report Menu options are clickable for the user or not
 * @param {*} report data object
 * @returns Object of Boolean values
 */
const reportMenu = (data, customerType = CHARTER, tabValue, isCustomDS) => {
	const { DISABLE_CONTEXT_SHARE: disableShare, DISABLE_CONTEXT_RUN_NOW: disableRunNow } =
		FEATURES[customerType].DASHBOARD;

	// Disable the add and edit button only if it is an unsaved custom dataset report
	const isUnsavedCustomDatasetReport = !data?.customDatasetId;
	// Edit option
	let isEditDisabled = false;
	if (tabValue === PROJECTS) {
		if (moment(data?.scheduleEndDate).isBefore(moment())) {
			isEditDisabled = true;
		}
		if (data?.status === RUNNING || data?.runQueryRequestObject === null) {
			isEditDisabled = true;
		}
		if (data?.status === SCHEDULED && data?.frequency === NOW) {
			isEditDisabled = true;
		}
		if (isCustomDS && isUnsavedCustomDatasetReport) {
			isEditDisabled = true;
		}
	}

	if (tabValue === SHARED) {
		if (isCustomDS) {
			isEditDisabled = true;
		}
	}

	// Download option
	let isDownloadDisabled = false;
	if (data?.status !== COMPLETED) {
		isDownloadDisabled = true;
	}

	// Run now option
	let isRunNowDisabled = false;
	if (moment(data?.scheduleEndDate).isBefore(moment())) {
		isRunNowDisabled = true;
	}
	if (data?.status === RUNNING) {
		isRunNowDisabled = true;
	}
	if (data?.status === COMPLETED && [NOW, LATER].includes(data?.frequency)) {
		isRunNowDisabled = true;
	}
	if (data?.status === SCHEDULED && data?.frequency === NOW) {
		isRunNowDisabled = true;
	}
	if (disableRunNow) {
		// For Basic users
		isRunNowDisabled = true;
	}

	// Sharing option
	let isSharingDisabled = false;
	if (data?.status === FAILED) {
		isSharingDisabled = true;
	}
	if (disableShare) {
		// For specific customer types, sharing report is disabled
		isSharingDisabled = true;
	}

	// Add to saved option
	let isAddToSavedDisabled = false;
	if (tabValue === PROJECTS) {
		if (isCustomDS && isUnsavedCustomDatasetReport) {
			isAddToSavedDisabled = true;
		}
	}
	if (tabValue === SHARED) {
		if (isCustomDS) {
			isAddToSavedDisabled = true;
		}
	}

	return {
		isEditDisabled,
		isDownloadDisabled,
		isRunNowDisabled,
		isSharingDisabled,
		isAddToSavedDisabled
	};
};

const callShrinkWindow = (_d, selectedTemp) => {
	/**
	 * Call shrink window to reduce preview payload to SHRINK_WINDOW_RANGE days and will also parent company
	 */
	const filterList = _d?.filtersWithValue;
	const selectedView = _d?.runQueryRequestObject?.view;
	const tmplBelongsToId = getTemplateId(selectedView);
	const linkItemActivity = _d?.linkItemActivity;
	const fieldList = buildShrinkTimeWindow(
		filterList,
		tmplBelongsToId,
		selectedView,
		selectedTemp,
		_d?.columns,
		linkItemActivity
	);
	return fieldList;
};

const getCustomAttrList = (_d) => {
	/**
	 * Need to build custom attribute separately.
	 */
	const filterList = _d?.filtersWithValue;
	const customAttrSelList = _d?.filtersWithValue?.filter((item) => {
		return item.parent === PARENT_CUST_ATTR && _d.columns.includes(item.businessName);
	});
	const list = buildCustomAttrList(filterList, customAttrSelList);
	return list;
};

// This creates the request body for the Edit.
// For the Preview, this request data will contain params for specified date limit
// Dead code
const prepareEditReqBody = (_d, selectedTemp) => {
	const {
		filtersWithValue,
		parentCompanyName,
		runQueryRequestObject: { filters, view }
	} = _d;
	const filterList = filtersWithValue;
	const compName = parentCompanyName;
	const tmplBelongsToId = getTemplateId(view);
	const isWeekSel = filterList?.find((el) => el.businessName === 'Week');
	const parentAndDftFilter = getParentAndDftFilter(filters, compName);
	const shrinkedTime = callShrinkWindow(_d, selectedTemp);
	const custAttrList = getCustomAttrList(_d);
	const forecastWk = buildForecastWk(isWeekSel, filterList, tmplBelongsToId);
	const formattedObj = Object.assign({}, ...custAttrList);
	const list = {
		...shrinkedTime,
		...formattedObj,
		...forecastWk,
		...parentAndDftFilter
	};
	return list;
};

const getSelectedList = (rowData, templateData, selectedTemplate) => {
	return buildPreviewSelectedList(rowData, templateData, selectedTemplate);
};

export { reportMenu, prepareEditReqBody, getSelectedList };
